// @flow
import { createStore, applyMiddleware, compose } from 'redux';
import thunkMiddleware from 'redux-thunk';
import rootReducer from './reducers';
import type { Action as ErrorAction } from './actions/error';
import type { Action as GMapsErrorAction } from './actions/gMapsError';
import type { Action as FlashMessagesAction } from './actions/flashMessages';
import type { Action as CurrentUserAction } from './actions/currentUser';
import type { Action as UserNotificationsAction } from './actions/userNotifications';
import type { Action as UserLocationAction } from './actions/userLocation';
import type { Action as PaymentMethodsAction } from './actions/paymentMethods';
import type { Action as InvitesAction } from './actions/invites';
import type { Action as ListingCountsAction } from './actions/listingCounts';
import type { Action as AreasOfFocusAction } from './actions/areasOfFocus';
import type { Action as JobFunctionsAction } from './actions/jobFunctions';
import type { Action as InternshipFunctionsAction } from './actions/internshipFunctions';
import type { Action as VolopFunctionsAction } from './actions/volopFunctions';
import type { Action as OrgsAction } from './actions/orgs';
import type { Action as ListingsAction } from './actions/listings';
import type { Action as GroupsAction } from './actions/groups';
import type { Action as IdealistDayEventsAction } from './actions/idealistDayEvents';
import type { Action as MainSearchAction } from './actions/mainSearch';
import type { Action as PostSearchAction } from './actions/postSearch';
import type { Action as ModalAction } from './actions/modal';
import type { Action as LayoutAction } from './actions/layout';
import type { Action as SearchRadiusAction } from './actions/searchRadius';
import type { Action as UserIdealistDayEventsAction } from './actions/userDashboard/idealistDayEvents';
import type { Action as OrgDashboardOrgsAction } from './actions/orgDashboard/orgs';
import type { Action as OrgDashboardListingsAction } from './actions/orgDashboard/listings';
import type { Action as OrgDashboardSearchAction } from './actions/orgDashboard/search';
import type { Action as OrgDashboardGradProgramsAction } from './actions/orgDashboard/gradPrograms';
import type { Action as OrgDashboardIdealistDayEventsAction } from './actions/orgDashboard/idealistDayEvents';
import type { Action as OrgDashboardIntegrationsAction } from './actions/orgDashboard/integrations';
import type { Action as GroupDashboardGroupsAction } from './actions/groupDashboard/groups';
import type { Action as GroupDashboardIdealistDayEventsAction } from './actions/groupDashboard/idealistDayEvents';
import type { Action as GroupDashboardListingsAction } from './actions/groupDashboard/listings';
import type { Action as GroupDashboardSearchAction } from './actions/groupDashboard/search';
import type { Action as InvoiceAction } from './actions/invoices';
import type { Action as PagesAction } from './actions/pages';
import type { Action as GradSchoolsProgramsAction } from './actions/gradSchools/gradPrograms';
import type { Action as DaysMapAction } from './actions/daysMap';
import type { Reducers } from './reducers';

type Action =
  | ErrorAction
  | GMapsErrorAction
  | FlashMessagesAction
  | CurrentUserAction
  | UserNotificationsAction
  | UserLocationAction
  | PaymentMethodsAction
  | InvitesAction
  | ListingCountsAction
  | AreasOfFocusAction
  | JobFunctionsAction
  | InternshipFunctionsAction
  | VolopFunctionsAction
  | OrgsAction
  | ListingsAction
  | GroupsAction
  | IdealistDayEventsAction
  | MainSearchAction
  | PostSearchAction
  | ModalAction
  | LayoutAction
  | SearchRadiusAction
  | UserIdealistDayEventsAction
  | OrgDashboardOrgsAction
  | OrgDashboardListingsAction
  | OrgDashboardSearchAction
  | OrgDashboardGradProgramsAction
  | OrgDashboardIdealistDayEventsAction
  | OrgDashboardIntegrationsAction
  | GroupDashboardGroupsAction
  | GroupDashboardListingsAction
  | GroupDashboardSearchAction
  | GroupDashboardIdealistDayEventsAction
  | InvoiceAction
  | PagesAction
  | GradSchoolsProgramsAction
  | DaysMapAction;

type $ExtractFunctionReturn = <V>(v: (...args: any) => V) => V;
export type State = $ObjMap<Reducers, $ExtractFunctionReturn>;
export type GetState = () => State;

// eslint-disable-next-line no-use-before-define
export type ThunkAction = (dispatch: Dispatch, getState: GetState) => any;
export type Dispatch = (action: Action | ThunkAction) => any;

export default function configureStore(preloadedState: Object = {}) {
  /* eslint-disable no-underscore-dangle */
  const composeEnhancers =
    typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      : compose;
  /* eslint-enable */

  const store = createStore<typeof preloadedState, Action, Dispatch>(
    rootReducer,
    preloadedState,
    // Setup redux dev tools
    composeEnhancers(applyMiddleware(thunkMiddleware))
  );

  return store;
}
